import { apiService } from './apiService';
import { TieredPackage as TieredPackageType } from '../types/tieredPricing';

export type TieredPackage = TieredPackageType;

// Normalize backend snake_case to frontend camelCase
function normalizePackage(data: any): TieredPackage {
    return {
        ...data,
        id: data.id,
        name: data.name || data.title,
        overview: data.overview || data.description,
        gallery: data.gallery || data.images || [],
        destinations: data.destinations || [],
        durationDays: data.duration_days || data.durationDays || (typeof data.duration === 'string' ? parseInt(data.duration) : 1),
        status: data.status,
        tierVariants: data.tier_variants || data.tierVariants,
        createdAt: data.created_at || data.createdAt,
        updatedAt: data.updated_at || data.updatedAt,
    };
}

class TieredPackageService {
    private cache: TieredPackage[] = [];
    private cacheTimestamp: number = 0;
    private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

    async getAllPackages(activeOnly: boolean = true): Promise<TieredPackage[]> {
        try {
            // Check cache
            if (this.cache.length > 0 && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
                console.log('📦 Returning cached tiered packages');
                return [...this.cache];
            }

            console.log('📡 Fetching tiered packages from backend...');
            const url = `/tiered-packages?activeOnly=${activeOnly}`;
            const response: any = await (apiService as any).request(url);

            if (response) {
                const rawPackages = Array.isArray(response) ? response : (response.data || []);
                this.cache = rawPackages.map(normalizePackage);
                this.cacheTimestamp = Date.now();
                console.log(`✅ Loaded ${this.cache.length} tiered packages`);
                return [...this.cache];
            }

            return [];
        } catch (error) {
            console.error('❌ Error fetching tiered packages:', error);
            return this.cache.length > 0 ? [...this.cache] : [];
        }
    }

    async getPackageById(id: string): Promise<TieredPackage | null> {
        try {
            // Check cache first
            const cached = this.cache.find(pkg => pkg.id === id);
            if (cached) {
                return cached;
            }

            console.log(`📡 Fetching tiered package: ${id}`);
            const response: any = await (apiService as any).request(`/tiered-packages/${id}`);

            if (response) {
                const rawPkg = response.data || response;
                const pkg = normalizePackage(rawPkg);
                // Update cache
                const existingIndex = this.cache.findIndex(p => p.id === id);
                if (existingIndex >= 0) {
                    this.cache[existingIndex] = pkg;
                } else {
                    this.cache.push(pkg);
                }
                return pkg;
            }

            return null;
        } catch (error) {
            console.error('❌ Error fetching tiered package:', error);
            return null;
        }
    }

    async createPackage(packageData: Omit<TieredPackage, 'id' | 'createdAt' | 'updatedAt'>): Promise<TieredPackage> {
        try {
            console.log('📡 Creating tiered package...');
            const response: any = await (apiService as any).request('/tiered-packages', {
                method: 'POST',
                body: JSON.stringify(packageData),
            });

            if (response) {
                const pkg = response.data || response;
                // Clear cache to force refresh
                this.clearCache();
                console.log('✅ Tiered package created:', pkg.id);
                return pkg;
            }

            throw new Error('Failed to create tiered package');
        } catch (error) {
            console.error('❌ Error creating tiered package:', error);
            throw error;
        }
    }

    async updatePackage(id: string, packageData: Partial<TieredPackage>): Promise<TieredPackage> {
        try {
            console.log(`📡 Updating tiered package: ${id}`);
            const response: any = await (apiService as any).request(`/tiered-packages/${id}`, {
                method: 'PUT',
                body: JSON.stringify(packageData),
            });

            if (response) {
                const pkg = response.data || response;
                // Update cache
                const index = this.cache.findIndex(p => p.id === id);
                if (index >= 0) {
                    this.cache[index] = pkg;
                }
                console.log('✅ Tiered package updated:', id);
                return pkg;
            }

            throw new Error('Failed to update tiered package');
        } catch (error) {
            console.error('❌ Error updating tiered package:', error);
            throw error;
        }
    }

    async updateStatus(id: string, status: 'DRAFT' | 'READY' | 'PUBLISHED'): Promise<TieredPackage> {
        try {
            console.log(`📡 Updating package status: ${id} -> ${status}`);
            const response: any = await (apiService as any).request(`/tiered-packages/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status }),
            });

            if (response) {
                const pkg = response.data || response;
                // Update cache
                const index = this.cache.findIndex(p => p.id === id);
                if (index >= 0) {
                    this.cache[index] = pkg;
                }
                console.log('✅ Package status updated');
                return pkg;
            }

            throw new Error('Failed to update package status');
        } catch (error) {
            console.error('❌ Error updating package status:', error);
            throw error;
        }
    }

    async deletePackage(id: string): Promise<boolean> {
        try {
            console.log(`📡 Deleting tiered package: ${id}`);
            await (apiService as any).request(`/tiered-packages/${id}`, {
                method: 'DELETE',
            });

            // Remove from cache
            this.cache = this.cache.filter(pkg => pkg.id !== id);
            console.log('✅ Tiered package deleted');
            return true;
        } catch (error) {
            console.error('❌ Error deleting tiered package:', error);
            throw error;
        }
    }

    clearCache(): void {
        this.cache = [];
        this.cacheTimestamp = 0;
        console.log('🗑️ Tiered packages cache cleared');
    }
}

export const tieredPackageService = new TieredPackageService();
export default tieredPackageService;
